import React from "react";
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { TopProduct } from "../../../types/analytics";

interface TopProductsListProps {
  title: string;
  products: TopProduct[];
}

const retailerColors: Record<string, string> = {
  amazon: "#ff9900",
  walmart: "#0071dc",
  costco: "#e31837",
};

const TopProductsList: React.FC<TopProductsListProps> = ({
  title,
  products,
}) => {
  const { t } = useTranslation();
  const maxQuantity = Math.max(...products.map((p) => p.quantitySold), 1);

  if (products.length === 0) {
    return (
      <Card sx={{ height: "100%" }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            {title}
          </Typography>
          <Typography color="text.secondary">{t("admin.analytics.noDataAvailable")}</Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ height: "100%" }}>
      <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          {title}
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {products.map((product, index) => {
            const barColor =
              retailerColors[product.retailer?.toLowerCase()] || "#6366f1";
            const barPercent = (product.quantitySold / maxQuantity) * 100;

            return (
              <Box
                key={product.productId + index}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  p: 1,
                  borderRadius: 1.5,
                  overflow: "hidden",
                  backgroundColor:
                    index % 2 === 0 ? "transparent" : "action.hover",
                  "&:hover": { backgroundColor: "action.selected" },
                }}
              >
                {/* Rank */}
                <Typography
                  sx={{
                    fontWeight: 800,
                    color: index < 3 ? barColor : "text.secondary",
                    minWidth: 24,
                    flexShrink: 0,
                    fontSize: "0.8rem",
                    textAlign: "center",
                  }}
                >
                  #{index + 1}
                </Typography>

                {/* Product Image */}
                <Avatar
                  src={product.image}
                  variant="rounded"
                  sx={{ width: 40, height: 40, flexShrink: 0 }}
                >
                  {product.title?.[0] || "P"}
                </Avatar>

                {/* Product Info — takes remaining space */}
                <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      display: "block",
                      maxWidth: "100%",
                    }}
                  >
                    {product.title}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      mt: 0.25,
                    }}
                  >
                    <Chip
                      label={product.retailer}
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: "0.6rem",
                        fontWeight: 700,
                        flexShrink: 0,
                        backgroundColor: `${barColor}18`,
                        color: barColor,
                        borderRadius: 1,
                      }}
                    />
                    <LinearProgress
                      variant="determinate"
                      value={barPercent}
                      sx={{
                        flex: 1,
                        height: 4,
                        borderRadius: 2,
                        backgroundColor: `${barColor}15`,
                        "& .MuiLinearProgress-bar": {
                          backgroundColor: barColor,
                          borderRadius: 2,
                        },
                      }}
                    />
                  </Box>
                </Box>

                {/* Revenue & Quantity — fixed right column */}
                <Box sx={{ textAlign: "right", flexShrink: 0, minWidth: 70 }}>
                  <Typography
                    variant="body2"
                    noWrap
                    sx={{ fontWeight: 700, color: "#10b981", fontSize: "0.8rem" }}
                  >
                    {product.revenue.toLocaleString("en-US", { style: "currency", currency: "USD" })}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {product.quantitySold} {t("admin.analytics.sold")}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      </CardContent>
    </Card>
  );
};

export default TopProductsList;
